import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, RotateCcw, LayoutTemplate, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { TAKEOVER_TOP, TAKEOVER_BOTTOM } from "@/components/CountdownTakeover";

const BE = process.env.REACT_APP_BACKEND_URL;
const src = (u) => (u ? (u.startsWith("http") ? u : `${BE}${u}`) : null);

const Thumb = ({ url, zones, label, testid }) => (
  <div className="w-[64px] shrink-0">
    <div className="relative rounded-sm overflow-hidden border border-race-line bg-race-bg" style={{ aspectRatio: "1080 / 2340" }} data-testid={testid}>
      {url && <img src={src(url)} alt={label} className="absolute inset-0 w-full h-full object-cover" />}
      {zones && <>
        <div className="absolute inset-x-0 bg-red-500/45" style={{ top: `${TAKEOVER_TOP * 100}%`, height: `${(TAKEOVER_BOTTOM - TAKEOVER_TOP) * 100}%` }} />
      </>}
    </div>
    <div className="text-[9px] font-mono uppercase text-race-muted text-center mt-0.5">{label}</div>
  </div>
);

const STATUS_CLS = { done: "text-green-500", failed: "text-red-500", running: "text-sky-500", queued: "text-race-muted", reverted: "text-amber-500" };

// Polls the latest regeneration job; exposes start() for the bulk button and per-campaign buttons.
export function useTakeoverJob(onChanged) {
  const [job, setJob] = useState(null);
  const prev = useRef(null);
  const load = () => api.get("/ads/admin/takeovers/jobs/latest").then((r) => {
    const j = r.data.job;
    if (prev.current === "running" && j?.status === "finished") { toast.success("Takeover regeneration finished"); onChanged?.(); }
    prev.current = j?.status;
    setJob(j);
  }).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (job?.status !== "running") return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [job?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  const start = async (ids) => {
    try { const { data } = await api.post("/ads/admin/takeovers/regenerate", ids ? { campaign_ids: ids } : {}); prev.current = "running"; setJob(data.job); toast.success(ids ? "Regenerating takeover…" : `Regenerating ${data.job.total} takeover(s)…`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const revert = async (cid) => {
    try { await api.post(`/ads/admin/takeovers/${cid}/revert`); toast.success("Reverted to the previous takeover"); load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return { job, start, revert, running: job?.status === "running" };
}

export function TakeoverRegenPanel({ tj }) {
  const { job, start, revert, running } = tj;
  const [zones, setZones] = useState(false);
  const [open, setOpen] = useState(true);
  const pct = job ? Math.round((job.done / Math.max(1, job.total)) * 100) : 0;
  return (
    <div className="bg-race-surface border border-race-line mb-3" data-testid="takeover-regen-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold">Countdown takeovers</div>
          <div className="text-xs text-race-sub">Redesign every takeover to the current safe zones (logo top 14%, message bottom 42%). Brand, copy and visuals are kept; previous versions can be reverted.</div>
        </div>
        <button onClick={() => start()} disabled={running} data-testid="takeover-regen-all"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-race-volt text-race-bg text-xs font-semibold hover:opacity-90 disabled:opacity-50 shrink-0">
          {running ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
          {running ? `Regenerating ${job.done}/${job.total}…` : "Regenerate all takeovers to safe zones"}
        </button>
      </div>
      {job && (
        <div className="border-t border-race-line px-4 py-3 space-y-3" data-testid="takeover-regen-job">
          <div className="flex items-center gap-3">
            <div className="flex-1 h-1.5 bg-race-elevated rounded-full overflow-hidden"><div className="h-full bg-race-volt transition-[width] duration-500" style={{ width: `${pct}%` }} data-testid="takeover-regen-progress" /></div>
            <span className="font-mono text-[11px] text-race-sub shrink-0">{job.status === "running" ? `${job.done}/${job.total}` : `Finished ${new Date(job.finished_at || job.started_at).toLocaleString()}`}</span>
            <button onClick={() => setZones((z) => !z)} data-testid="takeover-regen-zones"
              className={`inline-flex items-center gap-1 text-[11px] font-mono ${zones ? "text-race-volt" : "text-race-sub hover:text-race-txt"}`}><LayoutTemplate size={12} /> Check my design</button>
            <button onClick={() => setOpen((o) => !o)} className="text-[11px] font-mono text-race-sub hover:text-race-txt" data-testid="takeover-regen-toggle">{open ? "Hide" : "Show"}</button>
          </div>
          {open && (
            <div className="grid sm:grid-cols-2 gap-2">
              {job.items.map((it) => (
                <div key={it.campaign_id} className="flex items-center gap-3 bg-race-bg border border-race-line p-2" data-testid={`takeover-regen-item-${it.campaign_id}`}>
                  <Thumb url={it.old} zones={zones} label="Before" testid={`takeover-before-${it.campaign_id}`} />
                  <Thumb url={it.new} zones={zones} label="After" testid={`takeover-after-${it.campaign_id}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{it.name}</div>
                    <div className={`font-mono text-[10px] uppercase tracking-widest ${STATUS_CLS[it.status] || ""}`} data-testid={`takeover-regen-status-${it.campaign_id}`}>{it.status}</div>
                    {it.error && <div className="text-[11px] text-red-500">{it.error}</div>}
                    <div className="flex gap-3 mt-1">
                      {it.status === "done" && <button onClick={() => revert(it.campaign_id)} data-testid={`takeover-revert-${it.campaign_id}`}
                        className="inline-flex items-center gap-1 text-[11px] font-mono text-amber-500 hover:underline"><RotateCcw size={11} /> Revert</button>}
                      {it.status === "failed" && <button onClick={() => start([it.campaign_id])} disabled={running} data-testid={`takeover-retry-${it.campaign_id}`}
                        className="text-[11px] font-mono text-race-volt hover:underline">Try again</button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
